class BotDetector {
    constructor(options = {}) {
        this.options = {
            cookieName: 'bot_detected',
            cookieDuration: 30, // days
            suspiciousThreshold: 3, // number of red flags before marking as bot
            ...options
        };
        
        this.suspiciousPatterns = 0;
    }

    async detectBot() {
        const checks = [
            this.checkBehaviorPatterns(),
            this.checkTechnicalIndicators(),
            this.checkNetworkPatterns(),
            this.checkInputPatterns()
        ];

        const results = await Promise.all(checks);
        const totalPatterns = results.reduce((sum, patterns) => sum + patterns, 0);

        if (totalPatterns >= this.options.suspiciousThreshold) {
            this.setBlockCookie();
            return true;
        }
        return false;
    }

    checkBehaviorPatterns() {
        let patterns = 0;
        
        // Check mouse movement
        if (!this.hasNaturalMouseMovement()) patterns++;
        
        // Check scroll behavior
        if (!this.hasNaturalScrolling()) patterns++;
        
        // Check typing patterns
        if (!this.hasNaturalTypingSpeed()) patterns++;
        
        // Check form fill speed
        if (this.hasAbnormalFormFillSpeed()) patterns++;

        return patterns;
    }

    checkTechnicalIndicators() {
        let patterns = 0;
        
        // Check WebDriver
        if (navigator.webdriver) patterns++;
        
        // Check automation software
        if ('domAutomation' in window || 'domAutomationController' in window) patterns++;
        
        // Check headless browser indicators
        if (this.checkHeadlessBrowser()) patterns++;
        
        // Check if cookies are enabled
        if (!navigator.cookieEnabled) patterns++;

        return patterns;
    }

    async checkNetworkPatterns() {
        let patterns = 0;
        
        // Check request headers (requires server-side integration)
        const headers = await this.getRequestHeaders();
        if (this.hasSupiciousHeaders(headers)) patterns++;
        
        // Check IP reputation (requires API integration)
        if (await this.checkIPReputation()) patterns++;
        
        // Check request timing
        if (this.hasAbnormalRequestTiming()) patterns++;

        return patterns;
    }

    checkInputPatterns() {
        let patterns = 0;
        
        // Check for repeated values across different sessions
        if (this.hasRepeatedValues()) patterns++;
        
        // Check input format consistency
        if (this.hasUniformInputPattern()) patterns++;
        
        // Check for human-like variations in input
        if (!this.hasNaturalInputVariation()) patterns++;

        return patterns;
    }

    // Helper methods
    hasNaturalMouseMovement() {
        // Track mouse movement patterns
        let movements = [];
        document.addEventListener('mousemove', (e) => {
            movements.push({
                x: e.clientX,
                y: e.clientY,
                timestamp: Date.now()
            });
        });
        
        // Analyze for natural curves and speed variations
        return this.analyzeMousePatterns(movements);
    }

    hasNaturalScrolling() {
        let scrollEvents = [];
        let lastScroll = Date.now();
        
        document.addEventListener('scroll', () => {
            const now = Date.now();
            scrollEvents.push(now - lastScroll);
            lastScroll = now;
        });
        
        return scrollEvents.some(interval => interval > 100);
    }

    hasNaturalTypingSpeed() {
        let typingIntervals = [];
        let lastType = Date.now();
        
        document.addEventListener('keypress', () => {
            const now = Date.now();
            typingIntervals.push(now - lastType);
            lastType = now;
        });
        
        return this.analyzeTypingPatterns(typingIntervals);
    }

    setBlockCookie() {
        const date = new Date();
        date.setDate(date.getDate() + this.options.cookieDuration);
        document.cookie = `${this.options.cookieName}=true; expires=${date.toUTCString()}; path=/`;
    }
}

// Usage
const botDetector = new BotDetector({
    suspiciousThreshold: 4,
    cookieDuration: 60
});

// Add to form submission
document.querySelectorAll('form').forEach(form => {
    form.addEventListener('submit', async (e) => {
        const isBot = await botDetector.detectBot();
        if (isBot) {
            e.preventDefault();
            // Optional: Report to analytics or logging system
        }
    });
});

// Monitor general behavior
document.addEventListener('DOMContentLoaded', () => {
    botDetector.detectBot().then(isBot => {
        if (isBot) {
            // Take appropriate action
            console.log('Bot behavior detected');
        }
    });
});